/**
 * Chatbot `endpoint` is the side-menu URL, not a record URL.
 * `/merchants/inventory/new-products/edit/20122209` becomes
 * `/merchants/inventory/new-products`.
 *
 * Report submenu pages stay intact, including siblings that are not the
 * side-menu landing link:
 * `/merchants/store-reporting/new-sale-report/hourly-sales-report`.
 */

const MERCHANT_PREFIX = '/merchants';

/** Destinations from the side menu. Longest prefix wins. */
const SIDE_MENU_PATHS = [
  '/dashboard',
  '/web-register',
  '/order-new',
  '/customer-management',
  '/customer-management/customer-groups',
  '/loyalty-program',
  '/promotions/coupons/order-coupons',
  '/promotions/bogo-pricing',
  '/promotions/mix-and-match',
  '/customer-feed/posts',
  '/reporting',
  '/store-reporting/new-sale-report/sales-summary',
  '/store-reporting/inventory-report/recorder-inventory',
  '/store-reporting/customer-reports/customers',
  '/store-reporting/employee-reports/employee-list',
  '/store-reporting/payment-report',
  '/store-reporting/refund-report-main/order-refund-report',
  '/store-reporting/register-activity/register-closures',
  '/store-reporting/detailed-loyalty-points-report',
  '/store-reporting/store-credit-report',
  '/store-reporting/gift-card-report',
  '/store-reporting/new-taxes-report',
  '/store-reporting/location-report',
  '/store-reporting/item-sold-report',
  '/store-reporting/category-sold-report',
  '/store-reporting/po-products-report',
  '/store-reporting/uber-reports/delivery-report',
  '/store-reporting/uber-reports/delivery-charges',
  '/store-reporting/vendors-sales-reports',
  '/inventory/bulk-product-update',
  '/inventory/new-products',
  '/inventory/variants',
  '/inventory/composite-items',
  '/inventory/container-deposit',
  '/inventory/new-products',
  '/inventory/category',
  '/inventory/attributes',
  '/inventory/brands',
  '/inventory/tags',
  '/inventory/lottery',
  '/inventory/vendors',
  '/inventory/transfer',
  '/inventory/packaging',
  '/inventory/audit-log',
  '/stock-on-hand',
  '/purchase-order',
  '/stocktake-app',
  '/employee/manage-employees',
  '/employee/new-timesheet',
  '/taxes',
  '/store-settings/info',
  '/store-settings/options',
  '/store-settings/setup',
  '/store-settings/inventory',
  '/store-settings/Alters',
  '/store-settings/system-access',
  '/store-settings/breaks-and-compliance',
  '/store-settings/register',
  '/import-data',
  '/invertory-export',
  '/ask-quickvee-ai',
  '/zoho-connect',
  '/hq-connect',
  '/training',
  '/Digital-marketing-tags',
  '/inventory-merge',
];

const ACTION_EXACT = new Set([
  'add',
  'edit',
  'duplicate',
  'update',
  'create',
  'void',
  'completed',
  'operations',
  'new-transfer',
  'add-edit',
  'old',
]);

const isActionSegment = (segment) => {
  const value = String(segment || '').toLowerCase();
  if (!value) return false;
  if (ACTION_EXACT.has(value)) return true;
  return /^(add|edit|update|duplicate|create)([-_].+)?$/.test(value);
};

const isIdSegment = (segment) => {
  const value = String(segment || '');
  if (!value) return false;
  if (/^\d+$/.test(value)) return true;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value,
  );
};

const extractPathname = (rawPath) => {
  const raw = String(rawPath || '').trim();
  if (!raw) return '';
  try {
    if (/^https?:\/\//i.test(raw)) {
      return new URL(raw).pathname || '';
    }
  } catch {
    return '';
  }
  return raw.split(/[?#]/)[0];
};

const normalizeRelative = (pathname) => {
  let path = String(pathname || '').trim();
  if (!path.startsWith('/')) path = `/${path}`;
  path = path.replace(/\/{2,}/g, '/');
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
  if (path === MERCHANT_PREFIX || path.startsWith(`${MERCHANT_PREFIX}/`)) {
    path = path.slice(MERCHANT_PREFIX.length) || '/';
  }
  return path === '/' ? '' : path;
};

const matchesPrefix = (path, prefix) =>
  path === prefix || path.startsWith(`${prefix}/`);

const longestMenuMatch = (relative) => {
  let best = '';
  SIDE_MENU_PATHS.forEach((menuPath) => {
    if (!matchesPrefix(relative, menuPath)) return;
    if (menuPath.length > best.length) best = menuPath;
  });
  return best;
};

const stripDetailSegments = (relative) => {
  const parts = String(relative || '')
    .split('/')
    .filter(Boolean);
  const actionAt = parts.findIndex((part) => isActionSegment(part));
  const kept = actionAt === -1 ? parts : parts.slice(0, actionAt);
  while (kept.length > 0 && isIdSegment(kept[kept.length - 1])) {
    kept.pop();
  }
  return kept.length ? `/${kept.join('/')}` : '';
};

export const toChatbotEndpoint = (rawPath) => {
  const relative = normalizeRelative(extractPathname(rawPath));
  if (!relative) return `${MERCHANT_PREFIX}/order-new`;

  const legacyProductPath = '/inventory/products';
  if (
    (relative === legacyProductPath ||
      relative.startsWith(`${legacyProductPath}/`))
  ) {
    return `${MERCHANT_PREFIX}/inventory/new-products`;
  }

  const menuPath = longestMenuMatch(relative);
  if (menuPath) return `${MERCHANT_PREFIX}${menuPath}`;

  const trimmed = stripDetailSegments(relative);
  return `${MERCHANT_PREFIX}${trimmed || '/order-new'}`;
};

/**
 * Conversation bucket for the chatbot: one thread per side-menu page.
 * Derived from the same URL as `endpoint`, so a thread is never replayed
 * against a different page context on the backend.
 * `/merchants/inventory/new-products/edit/12` -> `inventory-new-products`
 * `/merchants/stock-on-hand` -> `stock-on-hand`
 */
export const toChatbotConversationScope = (rawPath) =>
  toChatbotEndpoint(rawPath)
    .slice(MERCHANT_PREFIX.length)
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
